const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

export const SET_SCOPE_TITLE = (roleName: string) => `Set Scope · ${roleName}`;

export const personProgress = (position: number, total: number) => `Person ${position} of ${total}`;

/**
 * Said per person rather than for the whole queue: the queue is a list of separate people in
 * separate entities, and one empty required box stops that person in that company only.
 */
export const stepAlert = (name: string, roleName: string) =>
  `${firstNameOf(name)} won't get any ${roleName} permissions until their scope is set.`;

/**
 * The boxes are the person's, not this role's. The access engine pools the boxes every role
 * they hold puts in play, so an answer here counts for all of them.
 */
export const APPLIES_TO_EVERY_ROLE =
  "This applies to every role they hold in this entity, not just this one.";

export const INCLUDE_BY = "Include By";
export const EXCLUDE_BY = "Exclude By";
export const SELECT_BOX_PLACEHOLDER = "Select..";
export const ADD_INCLUSION = "Add Inclusion";
export const ADD_EXCLUSION = "Add Exclusion";
/** Named, because every row carries one and "Remove this limit" reads the same on all of them. */
export const removeRuleLabel = (box: string | null, kind: "inclusion" | "exclusion") =>
  box ? `Remove the ${box} ${kind}` : `Remove this ${kind}`;

/** A half-made row would be dropped on save, so it is said on the row rather than swallowed. */
export const PICK_A_BOX = "Choose what to limit by, or remove this.";
export const PICK_VALUES = "Choose at least one, or remove this.";

export const INCOMPLETE_TITLE = "Finish the limits you started";
export const incompleteMessage = (entityNames: string[]) =>
  entityNames.length === 1
    ? `A limit in ${entityNames[0]} has nothing chosen. Finish it or remove it, then save.`
    : `Limits in ${entityNames.join(", ")} have nothing chosen. Finish them or remove them, then save.`;

/** Verbatim from the sketches' `RULE`, which states the engine's one-rule-per-box behaviour. */
export const RULE_LINE =
  "If you add an inclusion, you won't be able to add an exclusion of the same type (and vice-versa).";
export const RULE_EXAMPLE =
  "Example: if one client is included, every other client is excluded for this entity.";
export const NEEDS_AN_ANSWER = "Needs a value";
export const ENTITY_SET = "Set";

export const NO_OPTIONS = (boxLabel: string, entityName: string) =>
  `No ${boxLabel.toLowerCase()} in ${entityName} yet.`;

export const OPTIONS_FAILED =
  "Couldn't load these. Check your connection, then close and reopen this list.";

export const OPTIONS_NO_MATCH = "Nothing matches that search.";

export const SAVE_AND_NEXT = "Save & Next";
export const SAVE_AND_FINISH = "Save & Finish";
export const SAVE_AND_EXIT = "Save & Exit";
export const BACK = "Back";
export const SAVING = "Saving…";

export const saveFailedTitle = (name: string) => `Couldn't save ${firstNameOf(name)}'s scope`;

/**
 * A save is one request per box per entity, so an entity can be half written. The message never
 * claims more than was written, and never says "nothing changed" when something did.
 */
export const saveFailedMessage = (failedEntities: string[], somethingWasWritten: boolean) => {
  const who = failedEntities.join(", ");
  if (somethingWasWritten) {
    return `${who} saved only partly, so their access there may not be what you chose. Open it again and check.`;
  }
  return `Nothing was changed in ${who}. Try again.`;
};

export const stillBlockedTitle = (name: string) =>
  `Still no access for ${name.trim().split(/\s+/)[0] || name}`;

/**
 * The save went through and the engine still reports the person as having nothing. Saying
 * "Saved" alone here would be the silent no-op this branch has shipped before.
 */
export const savedButStillEmpty = (name: string, entityName: string) =>
  `Saved, but ${firstNameOf(name)} still has no access in ${entityName}. A required scope value is still empty.`;

export const QUEUE_EMPTY_TITLE = "Nobody to set scope for";
/**
 * Not "everyone has scope": a holder whose scope could not be read is left out of the queue, so
 * an empty queue only means nobody we could read is missing a value.
 */
export const QUEUE_EMPTY_MESSAGE =
  "Nobody we could check is missing a required scope value in this role.";

export const SCOPE_LOAD_FAILED_TITLE = "Couldn't load this person's scope";
export const SCOPE_LOAD_FAILED_MESSAGE =
  "Their current values didn't load, so nothing is shown to change. Try again.";

export const TRY_AGAIN = "Try Again";
